import { lazy, Suspense, useMemo } from "react";

import {
  HOME_BEREICHE,
  hatBereich,
  homeBereiche,
  type HomeBereich,
} from "@shared/home/rollen";

import { RollenUmschalter } from "@/components/home/cockpit/RollenUmschalter";
import { Skeleton } from "@/components/ui/skeleton";
import { usePersistedChoice } from "@/hooks/usePersistedChoice";
import { useUsers } from "@/hooks/useUsers";
import { useAuth } from "@/lib/auth/AuthContext";

// ---------------------------------------------------------------------------
// HomePlaceholder — rollenbasierter Home-Router.
// ---------------------------------------------------------------------------
// Session 17a: CEO + Superadmin live. Session 17b: PL + Buchhaltung.
//
// Die wirksame Rolle kommt direkt aus `user.role`: wenn Daniel impersoniert,
// hat der Server `user` auf den Ziel-User geswitcht (AuthContext.startImpersonate
// ruft fetchMe nach dem Impersonate-Toggle). Damit greift automatisch das
// Dashboard der impersonierten Rolle — genau was wir wollen.
//
// G-4: DashboardCEO laedt lazy — der Sandro-Home-Umbau (Heute-Karte, Meine
// Projekte, Verdienst-Puls, Partner-Schaufenster) haengt sonst im Entry-Chunk
// (size-limit stand bei 249/250 kB). Die geteilten Bausteine (PulseAdapter,
// NotificationCenter, Pipeline) blieben damals im Entry, weil die Buchhaltung
// sie eager brauchte — seit START-1 laedt auch sie nach, und die geteilten
// Bausteine sind ein gemeinsamer nachgeladener Teil aller vier Startseiten.
//
// ---------------------------------------------------------------------------
// ROLLE-1 (Daniel 2026-08-18): «Warum hat Sandro keine Werkbank? Ich habe
// schon 100x definiert, dass er CEO und Projektleiter ist.»
// ---------------------------------------------------------------------------
// Hier stand ein `switch (user.role)` — ein Mensch, ein Wert, ein Dashboard.
// Sandro ist `admin` und landete beim CEO-Cockpit; seine Werkbank hat ihm nie
// jemand gerendert, obwohl `/api/projects/werkbank` gar keinen Rollen-Riegel
// hat (der Weg war die ganze Zeit offen, es ging nur niemand hin). Gemessen
// fuehrt er dabei die meisten Vorgaenge im Haus: 122 Projekte, davon 93 in
// den Status, die die Werkbank zeigt.
//
// Die Verzweigung ist jetzt eine LISTE (`homeBereiche`). Wer einen Bereich
// hat, sieht exakt dasselbe wie vorher — kein Umschalter, kein Pixel anders.
// Wer zwei hat, bekommt beide, und der Bereich seiner Rolle bleibt der
// Einstieg: es wird nichts weggenommen, es kommt etwas dazu.
//
// Woher das Tier kommt: `useUsers()` → sich selbst finden → `isPlEligible`.
// Das ist derselbe Weg, den `UnassignedLeadsTile` seit LEAD-8 fuer dieselbe
// Frage geht, und `/api/users` ist fuer jeden angemeldeten Menschen offen.
// Bewusst NICHT ueber ein neues Feld auf `AuthenticatedUser`: nachgemessen
// kostet das 352 Typfehler in 87 Dateien, und ein Rollen-Array haette
// Berechtigungen angefasst, was hier niemand bestellt hat.

const DashboardCEO = lazy(() =>
  import("@/components/home/DashboardCEO").then((m) => ({
    default: m.DashboardCEO,
  })),
);

// ROLLE-1: die Werkbank laedt jetzt genauso nach wie das CEO-Cockpit daneben.
// Sie hing bis hierher fest im Startbuendel — also auch bei Sandro, Jacqueline
// und Daniel, die sie nie zu sehen bekamen. Mit der Doppelrolle ist das noch
// weniger haltbar: sonst traegt JEDER Mensch beide Startseiten mit sich herum,
// um immer nur eine davon zu sehen.
//
// Gemessen gibt das dem Startbuendel 13.08 kB gzip zurueck: 272.25 kB (245 B
// UEBER der 272-kB-Grenze — daran ist ROLLE-1 zuerst haengengeblieben) →
// 259.17 kB. Die Grenze wurde dafuer nicht angefasst.
const DashboardPL = lazy(() =>
  import("@/components/home/DashboardPL").then((m) => ({
    default: m.DashboardPL,
  })),
);

// KDO-1: der Kommandostand laedt lazy wie die beiden anderen Startseiten —
// er sieht genau einen Menschen (Daniel) und traegt Nutzung, Fahrplan und
// Gesundheit samt geteiltem Schema; im Startbuendel jedes Kollegen haette er
// nichts verloren. Gemessen: 275.22 kB (ueber der 272-kB-Grenze) → darunter.
const DashboardSuperadmin = lazy(() =>
  import("@/components/home/DashboardSuperadmin").then((m) => ({
    default: m.DashboardSuperadmin,
  })),
);

// START-1: die Buchhaltungs-Startseite war die letzte, die fest im
// Startbuendel hing — und mit ihr das ganze Meldungs-Center (jede Kartenzeile,
// jedes Modal dahinter), das damit auch bei jedem anderen Menschen und schon
// auf der Anmeldeseite mitlud. Jetzt laedt sie nach wie die drei anderen; das
// Meldungs-Center teilen sich alle vier als eigenen nachgeladenen Teil.
const DashboardBuchhaltung = lazy(() =>
  import("@/components/home/DashboardBuchhaltung").then((m) => ({
    default: m.DashboardBuchhaltung,
  })),
);

// Ein Geruest fuer alle nachgeladenen Startseiten — Kopfzeile plus zwei
// Bloecke. Alle beginnen mit einer Kopfleiste, also passt es auf alle.
function HomeFallback() {
  return (
    <div className="mx-auto flex min-h-full w-full max-w-page flex-col gap-6 p-8">
      <Skeleton className="h-16 w-64" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

export function HomePlaceholder() {
  const { user } = useAuth();

  // Die Frage stellt sich nur, wer nicht ohnehin in der Werkbank steht. Das
  // ist bewusst KEINE zweite Fassung der Eignungsregel (die steht allein in
  // `isPlEligible`) — es ist die Frage «habe ich den Bereich schon?», und die
  // gehoert dem Router. Fuer jeden reinen PL bleibt die Startseite damit ohne
  // zusaetzliche Abfrage.
  const brauchtHutFrage = user !== null && user.role !== "pl";
  const usersQuery = useUsers({ enabled: brauchtHutFrage });

  // Solange die Antwort aussteht, ist das Tier UNBEKANNT und geht als `null`
  // weiter. `homeBereiche` ist an dieser Stelle fail-closed: kein Tier, kein
  // PL-Hut. Lieber einen Wimpernschlag spaeter als einmal zu frueh bei
  // jemandem, der ihn nie haben darf.
  const bereiche = useMemo<readonly HomeBereich[]>(() => {
    if (!user) return [];
    const ich = (usersQuery.data ?? []).find((u) => u.id === user.id);
    return homeBereiche({ role: user.role, tier: ich?.tier ?? null });
  }, [user, usersQuery.data]);

  // Die Wahl wird ueber ALLE denkbaren Bereiche gespeichert, nicht ueber die
  // gerade erlaubten: `usePersistedChoice` liest den Speicher genau einmal —
  // waere die Liste hier die (waehrend des Ladens noch einelementige)
  // Auswahl, fiele Sandros gespeicherte Wahl bei jedem Laden auf die
  // Fuehrung zurueck.
  const [gewaehlt, setGewaehlt] = usePersistedChoice<HomeBereich>(
    "home.bereich",
    HOME_BEREICHE,
    "fuehrung",
  );

  // Geklemmt: gespeichert ist eine VORLIEBE, kein Zutritt. Was `homeBereiche`
  // nicht hergibt, kann aus dem localStorage nicht nachwachsen — auch nicht
  // von Hand editiert oder aus einem fremden Tab.
  const aktiv: HomeBereich | null =
    bereiche.length === 0
      ? null
      : hatBereich(bereiche, gewaehlt)
        ? gewaehlt
        : bereiche[0];

  if (!user || aktiv === null) return null;

  const umschalter =
    bereiche.length > 1 ? (
      <RollenUmschalter
        bereiche={bereiche}
        aktiv={aktiv}
        onWechsel={setGewaehlt}
      />
    ) : undefined;

  switch (aktiv) {
    case "kommandostand":
      return (
        <Suspense fallback={<HomeFallback />}>
          <DashboardSuperadmin />
        </Suspense>
      );
    case "buchhaltung":
      return (
        <Suspense fallback={<HomeFallback />}>
          <DashboardBuchhaltung />
        </Suspense>
      );
    case "projektleitung":
      return (
        <Suspense fallback={<HomeFallback />}>
          <DashboardPL rollenWechsel={umschalter} />
        </Suspense>
      );
    case "fuehrung":
      return (
        <Suspense fallback={<HomeFallback />}>
          <DashboardCEO rollenWechsel={umschalter} />
        </Suspense>
      );
  }
}
